import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Reveal, { EASE } from "./Reveal";
import { FoliageCluster, CornerFlourish } from "./Ornaments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ATTEND_OPTIONS = [
  { value: "yes", label: "Joyfully accepts" },
  { value: "no", label: "Regretfully declines" },
];

export default function Rsvp() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    attending: "yes",
    guests: 1,
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const openModal = () => {
    setStatus("idle");
    setError("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please share your name and email.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/rsvp`, {
        ...form,
        guests: Number(form.guests),
      });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    }
  };

  return (
    <section id="rsvp" className="rsvp" data-testid="rsvp-section">
      <Reveal>
        <div className="rsvp-frame">
          <div className="rsvp-corner rsvp-corner-tl">
            <CornerFlourish size={60} />
          </div>
          <div className="rsvp-corner rsvp-corner-tr">
            <CornerFlourish size={60} />
          </div>
          <div className="rsvp-foliage rf-l" aria-hidden="true">
            <FoliageCluster width={220} />
          </div>
          <div className="rsvp-foliage rf-r" aria-hidden="true">
            <FoliageCluster width={220} flip />
          </div>

          <div className="kicker rsvp-kicker">RSVP</div>
          <h2 className="rsvp-title serif">
            We&rsquo;d love
            <br />
            to have you.
          </h2>
          <p className="rsvp-sub">
            Come celebrate these beautiful days with us.
          </p>
          <button
            className="rsvp-open"
            data-testid="rsvp-open-button"
            onClick={openModal}
          >
            RSVP <span>→</span>
          </button>
        </div>
      </Reveal>

      <AnimatePresence>
        {open && (
          <motion.div
            className="rsvp-modal-veil"
            data-testid="rsvp-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <motion.div
              className="rsvp-modal-panel"
              data-lenis-prevent
              initial={{ opacity: 0, y: 42, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 26 }}
              transition={{ duration: 0.6, ease: EASE }}
            >
              <button
                className="rsvp-modal-close"
                data-testid="rsvp-modal-close"
                onClick={() => setOpen(false)}
              >
                Close
              </button>

              {status === "done" ? (
                <div className="rsvp-modal-done" data-testid="rsvp-success-message">
                  <div className="rsvp-kicker kicker">With gratitude</div>
                  <div className="rsvp-thanks serif">
                    Thank you, {form.name.trim().split(" ")[0]}.
                  </div>
                  <p className="rsvp-note">
                    {form.attending === "yes"
                      ? "Your response has been received — we can't wait to celebrate with you."
                      : "We'll miss you dearly — thank you for letting us know."}
                  </p>
                  <button className="rsvp-open" onClick={() => setOpen(false)}>
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <div className="rsvp-form-title serif">
                    Kindly respond
                  </div>
                  <div className="rsvp-form-sub">
                    A few details, and you&rsquo;re part of our day
                  </div>
                  <form onSubmit={submit} noValidate>
                    <div className="field-row">
                      <div className="field">
                        <label htmlFor="rsvp-name">Full Name *</label>
                        <input
                          id="rsvp-name"
                          data-testid="rsvp-name-input"
                          value={form.name}
                          onChange={set("name")}
                          placeholder="Your name"
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="rsvp-email">Email *</label>
                        <input
                          id="rsvp-email"
                          type="email"
                          data-testid="rsvp-email-input"
                          value={form.email}
                          onChange={set("email")}
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>

                    <div className="field">
                      <label>Will you join us?</label>
                      <div className="attend-options">
                        {ATTEND_OPTIONS.map((o) => (
                          <button
                            type="button"
                            key={o.value}
                            data-testid={`rsvp-attend-${o.value}`}
                            className={`attend-pill${form.attending === o.value ? " selected" : ""}`}
                            onClick={() =>
                              setForm((f) => ({ ...f, attending: o.value }))
                            }
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="field-row">
                      <div className="field">
                        <label htmlFor="rsvp-phone">Phone</label>
                        <input
                          id="rsvp-phone"
                          data-testid="rsvp-phone-input"
                          value={form.phone}
                          onChange={set("phone")}
                          placeholder="Optional"
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="rsvp-guests">Guests</label>
                        <select
                          id="rsvp-guests"
                          data-testid="rsvp-guests-select"
                          value={form.guests}
                          onChange={set("guests")}
                        >
                          {[1, 2, 3, 4, 5, 6].map((n) => (
                            <option key={n} value={n}>
                              {n} {n === 1 ? "guest" : "guests"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="field">
                      <label htmlFor="rsvp-message">A note for the couple</label>
                      <textarea
                        id="rsvp-message"
                        data-testid="rsvp-message-input"
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Your wishes (optional)"
                      />
                    </div>

                    {error && (
                      <div className="rsvp-error" data-testid="rsvp-error-message">
                        {error}
                      </div>
                    )}

                    <button
                      className="rsvp-submit"
                      data-testid="rsvp-submit-button"
                      disabled={status === "sending"}
                    >
                      {status === "sending" ? "Sending…" : "Send RSVP"}{" "}
                      <span>→</span>
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
